import { useEffect, useState } from 'react';

export interface NavigationRequest {
  waitUntil: (work: Promise<void>) => void;
}

export async function prepareNavigation(): Promise<void> {
  const pending: Promise<void>[] = [];
  window.dispatchEvent(
    new CustomEvent<NavigationRequest>('assist:before-navigation', {
      detail: { waitUntil: (work) => pending.push(work) },
    })
  );
  await Promise.all(pending);
}

export async function navigateWorkspace(
  updates: Record<string, string | null>,
  replace = false
): Promise<void> {
  await prepareNavigation();
  const url = new URL(window.location.href);
  for (const [key, value] of Object.entries(updates)) {
    if (value === null) url.searchParams.delete(key);
    else url.searchParams.set(key, value);
  }
  window.history[replace ? 'replaceState' : 'pushState'](null, '', url);
  window.dispatchEvent(new Event('assist:navigation'));
}

export function useWorkspaceLocation() {
  const [location, setLocation] = useState(() => window.location.href);
  useEffect(() => {
    const update = () => setLocation(window.location.href);
    let acceptedLocation = window.location.href;
    const historyNavigation = () => {
      const requestedLocation = window.location.href;
      void prepareNavigation()
        .then(() => {
          acceptedLocation = requestedLocation;
          setLocation(requestedLocation);
        })
        .catch(() => {
          // Keep the editor mounted with its failed save and original URL.
          window.history.pushState(null, '', acceptedLocation);
        });
    };
    const committedNavigation = () => {
      acceptedLocation = window.location.href;
      update();
    };
    window.addEventListener('assist:navigation', committedNavigation);
    window.addEventListener('popstate', historyNavigation);
    return () => {
      window.removeEventListener('assist:navigation', committedNavigation);
      window.removeEventListener('popstate', historyNavigation);
    };
  }, []);
  return new URL(location);
}
